import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {lastValueFrom} from 'rxjs';
import {SERVER_API_URL} from 'app/app.constants';
import {TurnAttachments} from 'app/blocks/service/assistant-context/assistant-attachments';
import {AssistantCapabilities} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-capabilities';
import {TurnExtras} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-analytics';

/** Only the non-empty attachment lists go into a request body (no legacy single `context` key anymore). */
function attachmentBody(attach: TurnAttachments | null | undefined): TurnAttachments {
    const body: TurnAttachments = {};
    if (attach?.contexts?.length) {
        body.contexts = attach.contexts;
    }
    if (attach?.screenshots?.length) {
        body.screenshots = attach.screenshots;
    }
    if (attach?.routes?.length) {
        body.routes = attach.routes;
    }
    return body;
}

export interface AssistantChatState {
    status: 'ACTIVE' | 'NONE' | 'UNAVAILABLE';
    sessionId: string;
    /** true when the CoPilot may propose changes (Plan → Approve) for this tenant; absent/false = read-only */
    planMode?: boolean;
    /** true when "Report a bug" is configured on the server (ClickUp token set); absent/false hides the action */
    bugReport?: boolean;
    /** the tenant's configured date format (e.g. "dd/MM/yyyy"); null/absent when none is set */
    dateFormat?: string | null;
    /** CU-14zefhy38pt W4: true when "request full history" tasks are configured; absent/false hides the button */
    historyRequest?: boolean | null;
    /** CU-14zefhy2xyz C4: what this login may do in the chat; absent from older servers (everything stays as before) */
    capabilities?: AssistantCapabilities | null;
}

/** CU-14zefhy38pt W4 (C3): the period a data answer covers (the server limits one question to `days` days). */
export interface AssistantDataWindow {
    days: number;
    from: string;
    to: string;
    /** a person may ask for the full history */
    requestable?: boolean;
}

export interface AssistantChatStarted {
    sessionId: string;
    taskId: string;
    /** the analytics id of the turn (feedback, UI events); absent on an older API or when the turn was not recorded */
    turnId?: string;
    error?: string;
}

export interface AssistantTaskStatus {
    status: string; // pending | running | completed | failed | cancelled
    session_id?: string;
    result?: {result?: string};
    error?: string;
    metadata?: {events?: any[]};
    /** set when a read turn proposes a change (MCPMagic tenants only); nothing is written until it is approved */
    pendingPlan?: AssistantPendingPlan;
    /** set while the assistant is waiting for a screenshot of the user's screen (needs the user's consent) */
    screenRequest?: {id: string; reason: string};
    /** completed approved turn: what it changed (fields with before/after values), for "What changed" + Undo */
    changes?: any[];
    /** with `changes`: "available" | "undone" | "expired" (undo window = 24 h from the turn's completion) */
    undoStatus?: string;
    /** a drafted bug report, when the server parses the `hm-bug` block itself (else the web reads it from the reply) */
    bugDraft?: any;
    /** RMA-61: while the turn runs, what it is doing (plain EN label from the tool name, tool calls so far, start epoch ms) */
    activity?: {label: string; step: number; startedAt?: number};
    /** CU-14zefhy38pt W4 (C3): the answer was limited to this period */
    dataWindow?: AssistantDataWindow;
    /** CU-14zefhy38pt W3: which tier and model answered (parsed with `parseAnsweredBy`) */
    answeredBy?: {tier?: string; model?: string};
}

/** A change the assistant proposes; the server keeps it per session for 30 minutes. */
export interface AssistantPendingPlan {
    id: string;
    summary: string;
    steps: string[];
}

/**
 * Talks to the HaulierMagic Assistant backend (hm `AssistantChatResource`), which proxies a read-only
 * Claude Code task in pythonmagic. A `session` key lets the panel keep several independent chats (tabs).
 */
@Injectable({providedIn: 'root'})
export class AssistantService {
    private readonly baseUrl = SERVER_API_URL + 'api/assistant/chat';

    constructor(private http: HttpClient) {}

    /** Does a conversation already exist for this session key? */
    getState(session: string): Promise<AssistantChatState> {
        return lastValueFrom(this.http.get<AssistantChatState>(this.baseUrl, {params: {session}}));
    }

    /**
     * Send a message; `fresh` = first turn of a (re)started conversation (sends the system preface).
     * `attach` = what the user attached to this message (CU-14zefhy1qnh): `contexts` (screens frozen with the 🧭 button,
     * max 5) and `screenshots` (uploaded `screen-*.jpg|png` names, max 3); keys are left out when empty, so a message
     * carries no screen context unless the user attached one. `attach.routes` = the in-app links hint (top level; first
     * message of a chat and whenever it changes).
     */
    sendMessage(
        session: string,
        message: string,
        fresh: boolean,
        attach?: TurnAttachments | null,
        extras?: TurnExtras | null
    ): Promise<AssistantChatStarted> {
        const body: any = {message, fresh, ...attachmentBody(attach), ...(extras || {})};
        return lastValueFrom(this.http.post<AssistantChatStarted>(this.baseUrl, body, {params: {session}}));
    }

    /**
     * Approve a pending plan: starts a write turn on the server. Returns the same shape as a chat turn; poll its
     * taskId with getEvents. Fails with 404/409 when the plan is unknown, expired or already used.
     */
    approvePlan(session: string, planId: string, attach?: TurnAttachments | null): Promise<AssistantChatStarted> {
        const body = attachmentBody(attach);
        return lastValueFrom(
            this.http.post<AssistantChatStarted>(`${this.baseUrl}/approve`, Object.keys(body).length ? body : null, {
                params: {session, planId}
            })
        );
    }

    /** Drop a pending plan without running it. */
    cancelPlan(session: string, planId: string): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/cancel`, null, {params: {session, planId}}));
    }

    /** Stop a running turn of this chat (204; 404 = not running or not this user's). */
    stopTurn(session: string, taskId: string): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/stop`, null, {params: {session, taskId}}));
    }

    /** CU-14zefhy26bz: one batch (<= 50) of UI events for analytics; callers drop failures. */
    sendEvents(session: string, events: any[], turnId?: string): Promise<any> {
        const body: any = {events};
        if (turnId) {
            body.turnId = turnId;
        }
        return lastValueFrom(this.http.post<any>(SERVER_API_URL + 'api/assistant/events', body, {params: {session}}));
    }

    /** CU-14zefhy26bz: a thumb on a reply (`kind: thumb`) or the outcome of the chat (`kind: resolution`); 409 = outcome already given. */
    sendFeedback(session: string, body: any): Promise<void> {
        return lastValueFrom(this.http.post<void>(SERVER_API_URL + 'api/assistant/feedback', body, {params: {session}}));
    }

    /** Poll a submitted task's status / result. */
    getEvents(taskId: string): Promise<AssistantTaskStatus> {
        return lastValueFrom(this.http.get<AssistantTaskStatus>(`${this.baseUrl}/events/${taskId}`));
    }

    /** List files uploaded into this chat session. */
    listFiles(session: string): Promise<any[]> {
        return lastValueFrom(this.http.get<any[]>(`${this.baseUrl}/files`, {params: {session}}));
    }

    /**
     * Download one chat file (a file the assistant produced, `outputs/...`) as a blob. A normal HttpClient call, so the
     * auth interceptor adds the bearer header; the token never goes into a URL. 404 = gone, 403 = not this user's.
     */
    downloadFile(session: string, path: string): Promise<Blob> {
        return lastValueFrom(this.http.get(`${this.baseUrl}/files/download`, {params: {session, path}, responseType: 'blob'}));
    }

    /**
     * Upload a file the assistant can read; returns the new file list. With `screenRequestId` the file answers the
     * assistant's screen request (the server saves it as `screen-<id>.png|jpg` so the waiting tool picks it up).
     */
    uploadFile(session: string, file: File | Blob, fileName?: string, screenRequestId?: string): Promise<any[]> {
        const form = new FormData();
        form.append('file', file, fileName || (file as File).name);
        const params: any = {session};
        if (screenRequestId) {
            params.screenRequestId = screenRequestId;
        }
        return lastValueFrom(this.http.post<any[]>(`${this.baseUrl}/files`, form, {params}));
    }

    /**
     * Tell the assistant the user said "Not now" to its screen request, so it stops waiting. 404 = already closed.
     * `reason` (optional, e.g. "screenshot timed out") says why no picture came when the capture itself failed.
     */
    declineScreen(session: string, id: string, reason?: string): Promise<void> {
        const params: any = {session, id};
        if (reason) {
            params.reason = reason;
        }
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/screen/decline`, null, {params}));
    }

    /** Remove an uploaded file by its session-relative path; returns the new file list. */
    deleteFile(session: string, path: string): Promise<any[]> {
        return lastValueFrom(this.http.delete<any[]>(`${this.baseUrl}/files`, {params: {session, path}}));
    }

    /**
     * File the bug report the user reviewed in the chat as a ClickUp task (CU-14zefhy1qnh item 7). Only on the user's
     * Submit click; 429 = rate limit. Returns the new task's link.
     */
    reportBug(session: string, body: AssistantBugReport): Promise<{taskId?: string; taskUrl?: string; warning?: string}> {
        return lastValueFrom(
            this.http.post<{taskId?: string; taskUrl?: string; warning?: string}>(SERVER_API_URL + 'api/assistant/bug-report', body, {
                params: {session}
            })
        );
    }

    /**
     * Ask for a plan that reverts an approved turn's changes (item 8). The plan comes back like any proposed change
     * (`pendingPlan`, or the plan itself) and still needs the user's approval. 409 = already undone.
     */
    undoChanges(session: string, taskId: string): Promise<any> {
        return lastValueFrom(this.http.post<any>(`${this.baseUrl}/undo`, null, {params: {session, taskId}}));
    }

    /**
     * The caller's chat sessions stored on the server (item 10): `[{key, label, updatedAt}]`; X3 (CU-14zefhy1r83): the
     * chats closed on any device follow as `{key, updatedAt (when closed), messages: 0, deleted: true}` (an older API
     * ignores the parameter).
     */
    listSessions(q?: string, includeArchived = false): Promise<any> {
        // CU-14zefhy38pt: `includeArchived` lists the chats past the 50 newest; `q` searches the titles (archived ones too)
        const params: Record<string, string> = {includeDeleted: 'true'};
        if (includeArchived) {
            params.includeArchived = 'true';
        }
        if (q && q.trim()) {
            params.q = q.trim().substring(0, 100);
        }
        return lastValueFrom(this.http.get<any>(`${this.baseUrl}/sessions`, {params}));
    }

    /** A session's stored transcript (404 = none). */
    getTranscript(session: string): Promise<any> {
        return lastValueFrom(this.http.get<any>(`${this.baseUrl}/transcript`, {params: {session}}));
    }

    /** Store a session's transcript (last-write-wins by `updatedAt`; 409 = the server has a newer one). */
    saveTranscript(session: string, transcript: any): Promise<any> {
        return lastValueFrom(this.http.put<any>(`${this.baseUrl}/transcript`, transcript, {params: {session}}));
    }

    /** CU-14zefhy38pt W4 (C2): "request full history": the server files a task for the period; returns `{taskId, taskUrl}`. */
    requestHistory(body: {session: string; question: string; from: string; to: string}): Promise<{taskId?: string; taskUrl?: string}> {
        return lastValueFrom(this.http.post<{taskId?: string; taskUrl?: string}>(SERVER_API_URL + 'api/assistant/data-window/request', body));
    }

    /** CU-14zefhy38pt W3 (C1): branches a chat from a message; `{session, label}` of the new chat (400 / 404 carry `{error}`). */
    forkChat(body: {session: string; fromMessageIndex: number}): Promise<{session: string; label?: string}> {
        return lastValueFrom(this.http.post<{session: string; label?: string}>(`${this.baseUrl}/fork`, body));
    }

    /** Forget a closed session's transcript on the server. */
    deleteTranscript(session: string): Promise<any> {
        return lastValueFrom(this.http.delete<any>(`${this.baseUrl}/transcript`, {params: {session}}));
    }
}

/** Body of `POST api/assistant/bug-report`. */
export interface AssistantBugReport {
    title: string;
    steps: string;
    expected: string;
    actual: string;
    contexts?: any[];
    screenshots?: string[];
}
